import { type ReactNode, type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import { CloseIcon } from '@instacart/ids-core'
// eslint-disable-next-line @retailer-platform/no-restricted-imports
import { FocusTrap, TextInput } from '@mantine/core'
import { StoreIcon } from '@instacart/ids-core'
import { ViewType } from '../retailer-scope-wrapper/ScopeSelection.types'
import { useDashMessages } from '../../../intl/intl.hooks'
import { type Option } from './utils'
import { StyledLogoBox, StyledLogoImage } from './Common'

type Props = {
  selectedState: Record<string, Option>
  setSelectedState: (selectedState: Record<string, Option>) => void
  multiSelect: boolean
  selectionType: ViewType
  scopeSelectionOptional?: boolean
  onClear: () => void
  defaultToAll?: boolean
  setTempSelectedState: (selectedState: Record<string, Option>) => void
  searchFilter: string
  setSearchFilter: (searchFilter: string) => void
  isOpen: boolean
}

const Container = styled.div({
  gap: '8px',
  display: 'flex',
  alignItems: 'center',
  height: '40px',
  width: '100%',
})

const ImageItem = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const TextItem = styled.div<{ strong?: boolean }>(({ strong, theme }) => ({
  display: 'flex',
  alignItems: 'center',
  userSelect: 'none',
  fontWeight: strong ? '600' : '400',
  fontSize: '14px',
  whiteSpace: 'nowrap',
  color: theme.tdsColors.systemGrayscale80,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

const PickerLabel: FunctionComponent<Props> = ({
  selectedState,
  setSelectedState,
  multiSelect,
  selectionType,
  scopeSelectionOptional,
  onClear,
  defaultToAll,
  setTempSelectedState,
  searchFilter,
  setSearchFilter,
  isOpen,
}) => {
  const intlMessages = useDashMessages({
    searchPlaceholder: 'multiScopePicker.search.placeholder',
    allBanners: 'multiScopePicker.labels.allBanners',
    selectBanner: 'multiScopePicker.labels.selectBanner',
    selectBannerSingular: 'multiScopePicker.labels.selectBannerSingular',
    bannersCount: 'multiScopePicker.labels.bannersCount',
    allSites: 'multiScopePicker.labels.allSites',
    selectSite: 'multiScopePicker.labels.selectSite',
    selectSiteSingular: 'multiScopePicker.labels.selectSiteSingular',
    sitesCount: 'multiScopePicker.labels.sitesCount',
    allSitesAndBanners: 'multiScopePicker.labels.allSitesAndBanners',
    selectSiteAndBanner: 'multiScopePicker.labels.selectSiteAndBanner',
    selectSiteAndBannerSingular: 'multiScopePicker.labels.selectSiteAndBannerSingular',
    sitesAndBannersCount: 'multiScopePicker.labels.sitesAndBannersCount',
    logoAlt: 'common.alt.logo',
  })
  let label: ReactNode
  if (multiSelect) {
    const selectedLeafNodes = Object.values(selectedState).filter(e => e.leafNode)
    if (selectionType === ViewType.RETAILER) {
      if (selectedLeafNodes.length === 0 && defaultToAll) {
        label = <TextItem>{intlMessages.allBanners}</TextItem>
      } else if (selectedLeafNodes.length === 0 && !defaultToAll) {
        label = <TextItem>{intlMessages.selectBanner}</TextItem>
      } else {
        label = (
          <TextItem strong>
            {intlMessages.bannersCount.replace('{count}', selectedLeafNodes.length.toString())}
          </TextItem>
        )
      }
    } else if (selectionType === ViewType.STORE_CONFIG) {
      if (selectedLeafNodes.length === 0 && defaultToAll) {
        label = <TextItem>{intlMessages.allSites}</TextItem>
      } else if (selectedLeafNodes.length === 0 && !defaultToAll) {
        label = <TextItem>{intlMessages.selectSite}</TextItem>
      } else {
        label = (
          <TextItem strong>
            {intlMessages.sitesCount.replace('{count}', selectedLeafNodes.length.toString())}
          </TextItem>
        )
      }
    } else {
      if (selectedLeafNodes.length === 0 && defaultToAll) {
        label = <TextItem>{intlMessages.allSitesAndBanners}</TextItem>
      } else if (selectedLeafNodes.length === 0 && !defaultToAll) {
        label = <TextItem>{intlMessages.selectSiteAndBanner}</TextItem>
      } else {
        label = (
          <TextItem strong>
            {intlMessages.sitesAndBannersCount.replace(
              '{count}',
              selectedLeafNodes.length.toString()
            )}
          </TextItem>
        )
      }
    }
  } else {
    const option = Object.values(selectedState)[0]
    if (selectionType === ViewType.RETAILER) {
      if (option) {
        label = (
          <ImageItem key={option.id}>
            {option.logoUrl && (
              <StyledLogoBox>
                <StyledLogoImage src={option.logoUrl} alt={intlMessages.logoAlt} />
              </StyledLogoBox>
            )}
            {option.type == 'banner' && !option.logoUrl && (
              <StyledLogoBox>
                <StoreIcon size={30} color="systemGrayscale30" />
              </StyledLogoBox>
            )}
            <TextItem strong>{option.name}</TextItem>
          </ImageItem>
        )
      } else {
        label = <TextItem>{intlMessages.selectBannerSingular}</TextItem>
      }
    } else if (selectionType === ViewType.STORE_CONFIG) {
      if (option) {
        label = <TextItem strong>{option.name}</TextItem>
      } else {
        label = <TextItem>{intlMessages.selectSiteSingular}</TextItem>
      }
    } else {
      if (option) {
        const siteOption = option.breadcrumbs[0]
        const bannerOption = option
        const name = [siteOption?.shortName || '', bannerOption.name].join(' > ')
        label = (
          <ImageItem>
            {bannerOption.logoUrl && (
              <StyledLogoBox>
                <StyledLogoImage src={option.logoUrl} alt={intlMessages.logoAlt} />
              </StyledLogoBox>
            )}
            {bannerOption.type == 'banner' && !bannerOption.logoUrl && (
              <StyledLogoBox>
                <StoreIcon size={30} color="systemGrayscale30" />
              </StyledLogoBox>
            )}
            <TextItem strong>{name}</TextItem>
          </ImageItem>
        )
      } else {
        label = <TextItem>{intlMessages.selectSiteAndBannerSingular}</TextItem>
      }
    }
  }

  return (
    <Container>
      {!isOpen && <>{label}</>}
      {isOpen && (
        <TextItem
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <FocusTrap active={true}>
            <TextInput
              style={{ width: '100%' }}
              styles={{
                input: {
                  fontSize: '15px',
                },
              }}
              placeholder={intlMessages.searchPlaceholder}
              variant="unstyled"
              onChange={e => {
                setSearchFilter(e.target.value)
              }}
            />
          </FocusTrap>
        </TextItem>
      )}

      {!isOpen && Object.values(selectedState).length > 0 && scopeSelectionOptional ? (
        <div
          css={{ marginLeft: '16px', display: 'flex', alignItems: 'center' }}
          data-testid="multi-scope-picker-clear-button"
          onClick={e => {
            e.stopPropagation()
            setTempSelectedState({})
            setSelectedState({})
            onClear()
          }}
        >
          <CloseIcon size={16} />
        </div>
      ) : null}
    </Container>
  )
}

export default PickerLabel
